import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { InvoiceService } from 'src/app/services/invoice/invoice.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-sales-commission',
  templateUrl: './add-sales-commission.component.html',
  styleUrls: ['./add-sales-commission.component.scss']
})
export class AddSalesCommissionComponent implements OnInit, OnDestroy {
  @Input() invoiceId: string = '';
  @Input() invoiceNumber: string = '';
  @Output() commissionAdded = new EventEmitter<void>();

  commissionForm: FormGroup;
  isLoading: boolean = false;

  constructor(
    private fb: FormBuilder,
    private _invoiceService: InvoiceService
  ) {
    this.commissionForm = this.fb.group({
      salesCommissionAmount: ['', [Validators.required, Validators.min(0)]],
      salesCommissionDate: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    // Form is already initialized in constructor
    this.setupModalEventListeners();
  }

  ngOnDestroy(): void {
    this.removeModalEventListeners();
  }

  private setupModalEventListeners(): void {
    const modalElement = document.getElementById('kt_modal_add_sales_commission');
    if (modalElement) {
      modalElement.addEventListener('hidden.bs.modal', () => {
        this.resetFormCompletely();
      });
    }
  }

  private removeModalEventListeners(): void {
    const modalElement = document.getElementById('kt_modal_add_sales_commission');
    if (modalElement) {
      modalElement.removeEventListener('hidden.bs.modal', () => {
        this.resetFormCompletely();
      });
    }
  }

  private resetFormCompletely(): void {
    this.commissionForm.reset();
    this.commissionForm.markAsUntouched();
    this.commissionForm.markAsPristine();

    // Reset each control individually
    Object.keys(this.commissionForm.controls).forEach(key => {
      const control = this.commissionForm.get(key);
      if (control) {
        control.setValue('');
        control.markAsUntouched();
        control.markAsPristine();
        control.setErrors(null);
      }
    });
  }

  onSubmit(): void {
    if (this.commissionForm.valid && this.invoiceId) {
      this.isLoading = true;

      const formData = this.commissionForm.getRawValue();
      const updateData = {
        sales_commission: formData.salesCommissionAmount,
        sales_commission_date: formData.salesCommissionDate
      };

      this._invoiceService.UpdateData(this.invoiceId, updateData).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          if (response.status) {
            Swal.fire({
              title: 'Success!',
              text: 'Sales commission has been added successfully.',
              icon: 'success',
              confirmButtonText: 'OK',
              buttonsStyling: false,
              customClass: {
                confirmButton: 'btn fw-bold btn-primary'
              }
            });
            this.closeModal();
            this.commissionAdded.emit();
          } else {
            this.showError('Failed to add sales commission. Please try again.');
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error adding sales commission:', error);
          this.showError('An error occurred while adding sales commission.');
        }
      });
    } else {
      this.markFormGroupTouched(this.commissionForm);
    }
  }

  private showError(message: string): void {
    Swal.fire({
      title: 'Error!',
      text: message,
      icon: 'error',
      confirmButtonText: 'OK',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn fw-bold btn-primary'
      }
    });
  }

  onCancel(): void {
    this.resetFormCompletely();
    this.closeModal();
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_add_sales_commission');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
    // The resetFormCompletely will be called by the hidden.bs.modal event listener
  }



  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }
}
